<template>
  <div v-if="showModule" class="training-module">
    <div class="training-container">
      <div class="training-content">
        <Tabs :value="activeTab" @update:value="activeTab = Number($event)" class="custom-tabs training-tabs">
          <TabList>
            <Tab :value="0">{{ t('wkt.client.training.tabs.catalog').toUpperCase() }}</Tab>
            <Tab :value="1">{{ t('wkt.client.training.tabs.enrollments').toUpperCase() }}</Tab>
            <Tab :value="2">{{ t('wkt.client.training.tabs.progress').toUpperCase() }}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel :value="0">
              <div class="toolbar-row">
                <InputText
                  v-model="catalogSearch"
                  class="search-input"
                  :placeholder="t('wkt.client.training.search.catalog')"
                />
                <div class="catalog-filters">
                  <Select
                    v-model="selectedCategoryFilter"
                    :options="categoryFilterOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="catalog-select"
                  />
                  <Select
                    v-model="selectedModeFilter"
                    :options="modeFilterOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="catalog-select"
                  />
                  <Select
                    v-model="selectedStatusFilter"
                    :options="statusFilterOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="catalog-select"
                  />
                </div>
                <Button
                  text
                  icon="pi pi-filter-slash"
                  :label="t('wkt.client.common.clear')"
                  class="training-link-button training-toolbar-button"
                  @click="clearCatalogFilters"
                />
              </div>

              <div v-if="isLoadingCatalog" class="loading-container">
                <ProgressSpinner />
              </div>

              <EmptyState
                v-else-if="filteredCatalog.length === 0"
                :title="t('wkt.client.training.empty.catalog.title')"
                :description="t('wkt.client.training.empty.catalog.description')"
              />

              <div v-else class="card-grid catalog-grid">
                <Card v-for="action in filteredCatalog" :key="action.id" class="training-card">
                  <template #header>
                    <div
                      class="training-card-media training-card-media--clickable"
                      :style="{ background: getTrainingCategoryGradient(action.trainingCategoryName) }"
                      role="button"
                      tabindex="0"
                      @click="openActionDetail(action.id)"
                      @keydown.enter.prevent="openActionDetail(action.id)"
                      @keydown.space.prevent="openActionDetail(action.id)"
                    >
                      <img
                        v-if="action.thumbnailPath"
                        :src="getThumbnailUrl(action.thumbnailPath)"
                        :alt="action.name"
                        class="training-card-image"
                      >
                      <div v-else class="training-card-fallback">
                        {{ getActionInitial(action.name) }}
                      </div>
                    </div>
                  </template>
                  <template #title>
                    <div class="card-title-row">
                      <div>
                        <div
                          class="card-title card-title--clickable"
                          role="button"
                          tabindex="0"
                          @click="openActionDetail(action.id)"
                          @keydown.enter.prevent="openActionDetail(action.id)"
                          @keydown.space.prevent="openActionDetail(action.id)"
                        >
                          {{ action.name }}
                        </div>
                        <div v-if="action.trainingCategoryName" class="card-subtitle">
                          {{ action.trainingCategoryName }}
                        </div>
                      </div>
                      <Tag :value="formatActionStatus(action.status)" severity="info" />
                    </div>
                  </template>
                  <template #content>
                    <div class="action-chip-row">
                      <Tag
                        :value="formatMode(action.mode)"
                        severity="secondary"
                      />
                      <Tag
                        v-if="action.requiresApproval"
                        :value="t('wkt.client.training.chips.requires_approval')"
                        severity="warn"
                      />
                      <Tag
                        v-else-if="action.manualSelection"
                        :value="t('wkt.client.training.chips.manual_selection')"
                        severity="info"
                      />
                      <Tag
                        v-else
                        :value="t('wkt.client.training.chips.direct_enrollment')"
                        severity="success"
                      />
                      <Tag
                        v-if="action.prerequisiteRequired && !action.prerequisiteMet"
                        :value="t('wkt.client.training.chips.prerequisite_pending')"
                        severity="danger"
                      />
                    </div>

                    <div class="detail-list">
                      <div v-if="action.courseName" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.course') }}</span>
                        <span class="detail-value">{{ action.courseName }}</span>
                      </div>
                      <div v-if="action.realizationStart || action.realizationEnd" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.realization') }}</span>
                        <span class="detail-value">
                          {{ formatDateRange(action.realizationStart, action.realizationEnd) }}
                        </span>
                      </div>
                      <div v-if="action.enrollmentStart || action.enrollmentEnd" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.enrollment') }}</span>
                        <span class="detail-value">
                          {{ formatDateRange(action.enrollmentStart, action.enrollmentEnd) }}
                        </span>
                      </div>
                      <div v-if="action.locationText" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.location') }}</span>
                        <span class="detail-value">{{ action.locationText }}</span>
                      </div>
                      <div v-if="getCapacityText(action)" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.capacity') }}</span>
                        <span class="detail-value">{{ getCapacityText(action) }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.availability') }}</span>
                        <span class="detail-value">
                          <span :class="['availability-chip', getAvailabilityClass(action)]">
                            {{ getAvailabilityText(action) }}
                          </span>
                        </span>
                      </div>
                      <div v-if="action.prerequisiteRequired && action.prerequisiteCourseName" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.prerequisite') }}</span>
                        <span class="detail-value">
                          {{ getPrerequisiteText(action) }}
                        </span>
                      </div>
                      <div v-if="action.sequenceProgramName" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.program') }}</span>
                        <span class="detail-value">{{ action.sequenceProgramName }}</span>
                      </div>
                      <div v-if="action.sequenceOrder && action.sequenceTotalCourses" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.sequence') }}</span>
                        <span class="detail-value">{{ getSequenceText(action) }}</span>
                      </div>
                    </div>

                    <p v-if="action.description" class="description">
                      {{ action.description }}
                    </p>

                    <div class="card-actions">
                      <Button
                        text
                        icon="pi pi-eye"
                        :label="t('wkt.client.training.action.view_detail')"
                        class="training-link-button"
                        @click="openActionDetail(action.id)"
                      />
                      <Button
                        v-if="shouldShowEnrollButton(action)"
                        text
                        icon="pi pi-send"
                        :label="getEnrollButtonLabel(action)"
                        class="training-link-button"
                        @click="openEnrollmentDialog(action)"
                      />
                    </div>
                    <small v-if="getCurrentEnrollment(action.id)" class="helper-text">
                      {{ t('wkt.client.training.already_managed') }}:
                      {{ formatEnrollmentStatus(getCurrentEnrollment(action.id)?.status) }}
                    </small>
                  </template>
                </Card>
              </div>
            </TabPanel>

            <TabPanel :value="1">
              <div class="toolbar-row">
                <InputText
                  v-model="enrollmentSearch"
                  class="search-input"
                  :placeholder="t('wkt.client.training.search.enrollments')"
                />
                <div class="catalog-filters">
                  <Select
                    v-model="selectedEnrollmentView"
                    :options="enrollmentViewOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="catalog-select"
                  />
                </div>
              </div>

              <div v-if="isLoadingEnrollments" class="loading-container">
                <ProgressSpinner />
              </div>

              <EmptyState
                v-else-if="filteredEnrollments.length === 0"
                :title="t('wkt.client.training.empty.enrollments.title')"
                :description="t('wkt.client.training.empty.enrollments.description')"
              />

              <div v-else class="card-grid">
                <Card v-for="enrollment in filteredEnrollments" :key="enrollment.id" class="training-card">
                  <template #title>
                    <div class="card-title-row">
                      <div>
                        <div
                          class="card-title card-title--clickable"
                          role="button"
                          tabindex="0"
                          @click="openActionDetail(enrollment.trainingActionId)"
                          @keydown.enter.prevent="openActionDetail(enrollment.trainingActionId)"
                          @keydown.space.prevent="openActionDetail(enrollment.trainingActionId)"
                        >
                          {{ enrollment.trainingActionName }}
                        </div>
                        <div v-if="enrollment.trainingCategoryName" class="card-subtitle">
                          {{ enrollment.trainingCategoryName }}
                        </div>
                      </div>
                      <Tag
                        :value="formatEnrollmentStatus(enrollment.status)"
                        :severity="getEnrollmentSeverity(enrollment.status)"
                      />
                    </div>
                  </template>
                  <template #content>
                    <div class="detail-list">
                      <div v-if="enrollment.enrollmentDate" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.enrollment_date') }}</span>
                        <span class="detail-value">{{ formatSimpleDate(enrollment.enrollmentDate) }}</span>
                      </div>
                      <div v-if="enrollment.resultDate" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.result_date') }}</span>
                        <span class="detail-value">{{ formatSimpleDate(enrollment.resultDate) }}</span>
                      </div>
                      <div v-if="enrollment.resultValue" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.result') }}</span>
                        <span class="detail-value">{{ enrollment.resultValue }}</span>
                      </div>
                      <div v-if="enrollment.certificateDocumentName" class="detail-row">
                        <span class="detail-label">{{ t('wkt.client.training.fields.certificate') }}</span>
                        <span class="detail-value">{{ enrollment.certificateDocumentName }}</span>
                      </div>
                    </div>

                    <div class="card-actions">
                      <Button
                        text
                        icon="pi pi-eye"
                        :label="t('wkt.client.training.action.view_detail')"
                        class="training-link-button"
                        @click="openActionDetail(enrollment.trainingActionId)"
                      />
                      <Button
                        v-if="enrollment.certificateDocumentId"
                        text
                        icon="pi pi-download"
                        :label="t('wkt.client.training.action.download_certificate')"
                        class="training-link-button"
                        @click="downloadEnrollmentCertificate(enrollment)"
                      />
                    </div>
                  </template>
                </Card>
              </div>
            </TabPanel>

            <TabPanel :value="2">
              <div v-if="isLoadingProgress" class="loading-container">
                <ProgressSpinner />
              </div>

              <EmptyState
                v-else-if="enrollments.length === 0"
                :title="t('wkt.client.training.empty.progress.title')"
                :description="t('wkt.client.training.empty.progress.description')"
              />

              <div v-else class="progress-layout">
                <div class="progress-summary-grid">
                  <Card class="summary-card">
                    <template #content>
                      <div class="summary-value">{{ progressSummary.completed }}</div>
                      <div class="summary-label">{{ t('wkt.client.training.progress.completed') }}</div>
                    </template>
                  </Card>
                  <Card class="summary-card">
                    <template #content>
                      <div class="summary-value">{{ progressSummary.inProgress }}</div>
                      <div class="summary-label">{{ t('wkt.client.training.progress.in_progress') }}</div>
                    </template>
                  </Card>
                  <Card class="summary-card">
                    <template #content>
                      <div class="summary-value">{{ progressSummary.pending }}</div>
                      <div class="summary-label">{{ t('wkt.client.training.progress.pending') }}</div>
                    </template>
                  </Card>
                  <Card class="summary-card">
                    <template #content>
                      <div class="summary-value">{{ progressSummary.successRate }}%</div>
                      <div class="summary-label">{{ t('wkt.client.training.progress.success_rate') }}</div>
                    </template>
                  </Card>
                </div>

                <Card class="training-card">
                  <template #title>
                    {{ t('wkt.client.training.progress.by_category') }}
                  </template>
                  <template #content>
                    <div class="progress-list">
                      <div v-for="item in progressByCategory" :key="item.key" class="progress-row">
                        <div class="progress-row-header">
                          <span>{{ item.label }}</span>
                          <span>{{ item.completed }}/{{ item.total }}</span>
                        </div>
                        <ProgressBar :value="item.percent" />
                        <div v-if="item.courses?.length" class="progress-row-actions">
                          <Button
                            text
                            size="small"
                            :icon="isProgressCategoryExpanded(item.key) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                            :label="isProgressCategoryExpanded(item.key)
                              ? t('wkt.client.training.progress.hide_courses')
                              : t('wkt.client.training.progress.show_courses')"
                            class="training-link-button training-progress-toggle"
                            @click="toggleProgressCategory(item.key)"
                          />
                        </div>
                        <div v-if="isProgressCategoryExpanded(item.key) && item.courses?.length" class="progress-course-list">
                          <div
                            v-for="course in item.courses"
                            :key="`${item.key}-${course.courseId}`"
                            class="progress-course-row"
                          >
                            <span class="progress-course-label">{{ course.label }}</span>
                            <Tag
                              :value="course.completed
                                ? t('wkt.client.training.progress.course_completed')
                                : t('wkt.client.training.progress.course_pending')"
                              :severity="course.completed ? 'success' : 'secondary'"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </Card>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>

    <Dialog
      v-model:visible="detailDialogVisible"
      modal
      :header="selectedActionDetail?.name || t('wkt.client.training.detail.title')"
      :style="{ width: '720px', maxWidth: '95vw' }"
    >
      <div v-if="isLoadingDetail" class="loading-container">
        <ProgressSpinner />
      </div>
      <div v-else-if="selectedActionDetail" class="detail-dialog-content">
        <div class="action-chip-row detail-chip-row">
          <Tag
            :value="formatMode(selectedActionDetail.mode)"
            severity="secondary"
          />
          <Tag
            v-if="selectedActionDetail.requiresApproval"
            :value="t('wkt.client.training.chips.requires_approval')"
            severity="warn"
          />
          <Tag
            v-else-if="selectedActionDetail.manualSelection"
            :value="t('wkt.client.training.chips.manual_selection')"
            severity="info"
          />
          <Tag
            v-else
            :value="t('wkt.client.training.chips.direct_enrollment')"
            severity="success"
          />
          <Tag
            v-if="selectedActionDetail.prerequisiteRequired && !selectedActionDetail.prerequisiteMet"
            :value="t('wkt.client.training.chips.prerequisite_pending')"
            severity="danger"
          />
        </div>
        <div class="detail-list">
          <div v-if="selectedActionDetail.trainingCategoryName" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.category') }}</span>
            <span class="detail-value">{{ selectedActionDetail.trainingCategoryName }}</span>
          </div>
          <div v-if="selectedActionDetail.courseName" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.course') }}</span>
            <span class="detail-value">{{ selectedActionDetail.courseName }}</span>
          </div>
          <div v-if="selectedActionDetail.scheduleText" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.schedule') }}</span>
            <span class="detail-value">{{ selectedActionDetail.scheduleText }}</span>
          </div>
          <div v-if="selectedActionDetail.locationText" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.location') }}</span>
            <span class="detail-value">{{ selectedActionDetail.locationText }}</span>
          </div>
          <div v-if="selectedActionDetail.instructorText" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.instructor') }}</span>
            <span class="detail-value">{{ selectedActionDetail.instructorText }}</span>
          </div>
          <div v-if="selectedActionDetail.durationHours" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.duration') }}</span>
            <span class="detail-value">{{ formatHours(selectedActionDetail.durationHours) }}</span>
          </div>
          <div v-if="getCapacityText(selectedActionDetail)" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.capacity') }}</span>
            <span class="detail-value">{{ getCapacityText(selectedActionDetail) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.availability') }}</span>
            <span class="detail-value">
              <span :class="['availability-chip', getAvailabilityClass(selectedActionDetail)]">
                {{ getAvailabilityText(selectedActionDetail) }}
              </span>
            </span>
          </div>
          <div v-if="selectedActionDetail.prerequisiteRequired && selectedActionDetail.prerequisiteCourseName" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.prerequisite') }}</span>
            <span class="detail-value">{{ getPrerequisiteText(selectedActionDetail) }}</span>
          </div>
          <div v-if="selectedActionDetail.sequenceProgramName" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.program') }}</span>
            <span class="detail-value">{{ selectedActionDetail.sequenceProgramName }}</span>
          </div>
          <div v-if="selectedActionDetail.sequenceOrder && selectedActionDetail.sequenceTotalCourses" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.sequence') }}</span>
            <span class="detail-value">{{ getSequenceText(selectedActionDetail) }}</span>
          </div>
          <div v-if="selectedActionDetail.nextCourseName" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.next_course') }}</span>
            <span class="detail-value">{{ selectedActionDetail.nextCourseName }}</span>
          </div>
          <div v-if="selectedActionDetail.realizationStart || selectedActionDetail.realizationEnd" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.realization') }}</span>
            <span class="detail-value">
              {{ formatDateRange(selectedActionDetail.realizationStart, selectedActionDetail.realizationEnd) }}
            </span>
          </div>
        </div>
        <p v-if="selectedActionDetail.description" class="description detail-description">
          {{ selectedActionDetail.description }}
        </p>
        <div
          v-if="getObjectivesProgramHtml(selectedActionDetail)"
          class="detail-rich-text"
        >
          <h4 class="detail-rich-text__title">
            {{ t('wkt.client.training.fields.objectives_program') }}
          </h4>
          <div
            class="detail-rich-text__content"
            v-html="getObjectivesProgramHtml(selectedActionDetail)"
          ></div>
        </div>
        <div v-if="shouldShowEnrollButton(selectedActionDetail)" class="card-actions detail-actions">
          <Button
            text
            icon="pi pi-send"
            :label="getEnrollButtonLabel(selectedActionDetail)"
            class="training-link-button"
            @click="openEnrollmentDialog(selectedActionDetail)"
          />
        </div>
        <small v-if="getCurrentEnrollment(selectedActionDetail.id)" class="helper-text">
          {{ t('wkt.client.training.already_managed') }}:
          {{ formatEnrollmentStatus(getCurrentEnrollment(selectedActionDetail.id)?.status) }}
        </small>
      </div>
    </Dialog>

    <Dialog
      v-model:visible="enrollmentDialogVisible"
      modal
      :header="getEnrollmentDialogTitle()"
      :style="{ width: '560px', maxWidth: '95vw' }"
    >
      <div v-if="pendingEnrollmentAction" class="detail-dialog-content">
        <div class="detail-list">
          <div class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.course') }}</span>
            <span class="detail-value">{{ pendingEnrollmentAction.name }}</span>
          </div>
          <div v-if="pendingEnrollmentAction.trainingCategoryName" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.category') }}</span>
            <span class="detail-value">{{ pendingEnrollmentAction.trainingCategoryName }}</span>
          </div>
          <div v-if="pendingEnrollmentAction.realizationStart || pendingEnrollmentAction.realizationEnd" class="detail-row">
            <span class="detail-label">{{ t('wkt.client.training.fields.realization') }}</span>
            <span class="detail-value">
              {{ formatDateRange(pendingEnrollmentAction.realizationStart, pendingEnrollmentAction.realizationEnd) }}
            </span>
          </div>
        </div>

        <p class="description detail-description">
          {{ getEnrollmentDialogDescription() }}
        </p>

        <div class="detail-rich-text">
          <h4 class="detail-rich-text__title">
            {{ t('wkt.client.training.enroll_dialog.observations') }}
          </h4>
          <Textarea
            v-model="pendingEnrollmentObservations"
            rows="4"
            maxlength="2000"
            :placeholder="t('wkt.client.training.enroll_dialog.observations_placeholder')"
            class="w-full"
          />
        </div>
      </div>

      <template #footer>
        <Button
          text
          class="training-link-button"
          :label="t('wkt.client.common.dialog-buttons.cancel')"
          :disabled="isSubmittingEnrollment"
          @click="closeEnrollmentDialog"
        />
        <Button
          class="training-primary-button"
          :label="getEnrollButtonLabel(pendingEnrollmentAction)"
          :loading="isSubmittingEnrollment"
          :disabled="!pendingEnrollmentAction"
          @click="submitEnrollment"
        />
      </template>
    </Dialog>

    <Toast position="bottom-center" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { gwtApi } from '@wkt/api/gwt/gwtAPI'
import { worktimeApi } from '@wkt/api/worktime'
import { useAlert } from '@wkt/composables/useAlert'
import { useDateConverter } from '@wkt/composables/useDateConverter'
import { useDateFormatter } from '@wkt/composables/useDateFormatter'
import { useUtils } from '@wkt/composables/useUtils'
import EmptyState from '@wkt/components/common/EmptyState.vue'

const { t } = useI18n()
const { showAlert } = useAlert()
const { convertSimpleDateToDate } = useDateConverter()
const { formatOnlyDate } = useDateFormatter()
const { getPublicAssetUrl } = useUtils()

const TRAINING_ACTION_STATUS_OPEN = 1
const TRAINING_ACTION_STATUS_IN_PROGRESS = 3
const TRAINING_ENROLLMENT_STATUS_PENDING_APPROVAL = 0
const TRAINING_ENROLLMENT_STATUS_PENDING_SELECTION = 1
const TRAINING_ENROLLMENT_STATUS_ENROLLED = 2
const TRAINING_ENROLLMENT_STATUS_REJECTED = 3
const TRAINING_ENROLLMENT_STATUS_COMPLETED = 4
const TRAINING_ENROLLMENT_STATUS_NOT_PASS = 5
const TRAINING_ENROLLMENT_STATUS_NO_SHOW = 6
const TRAINING_ENROLLMENT_STATUS_CANCELED = 7

const showModule = ref(false)
const activeTab = ref(0)
const catalogSearch = ref('')
const enrollmentSearch = ref('')
const selectedEnrollmentView = ref('all')
const selectedCategoryFilter = ref('all')
const selectedModeFilter = ref('all')
const selectedStatusFilter = ref('all')
const isLoadingCatalog = ref(false)
const isLoadingEnrollments = ref(false)
const isLoadingProgress = ref(false)
const isLoadingDetail = ref(false)
const catalog = ref<any[]>([])
const enrollments = ref<any[]>([])
const progressData = ref<any | null>(null)
const sessionInformation = ref<any>(null)
const detailDialogVisible = ref(false)
const selectedActionDetail = ref<any | null>(null)
const enrollmentDialogVisible = ref(false)
const pendingEnrollmentAction = ref<any | null>(null)
const pendingEnrollmentObservations = ref('')
const isSubmittingEnrollment = ref(false)
const expandedProgressCategoryKeys = ref<string[]>([])

const isCorporate = computed(() => Number(sessionInformation.value?.company?.licenseType) === 2)

const activeEnrollmentsByActionId = computed(() => {
  const index = new Map<number, any>()
  enrollments.value.forEach((enrollment) => {
    if (!enrollment?.trainingActionId) {
      return
    }
    if ([TRAINING_ENROLLMENT_STATUS_REJECTED, TRAINING_ENROLLMENT_STATUS_CANCELED].includes(Number(enrollment.status))) {
      return
    }
    index.set(Number(enrollment.trainingActionId), enrollment)
  })
  return index
})

const filteredCatalog = computed(() => {
  const query = catalogSearch.value.trim().toLowerCase()
  return catalog.value.filter((action) => {
    const matchesSearch = !query || [action.name, action.trainingCategoryName, action.courseName, action.locationText]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query))

    const matchesCategory = selectedCategoryFilter.value === 'all'
      || String(action.trainingCategoryName || '') === selectedCategoryFilter.value

    const matchesMode = selectedModeFilter.value === 'all'
      || String(action.mode ?? '') === selectedModeFilter.value

    const matchesStatus = selectedStatusFilter.value === 'all'
      || String(action.status ?? '') === selectedStatusFilter.value

    return matchesSearch && matchesCategory && matchesMode && matchesStatus
  })
})

const filteredEnrollments = computed(() => {
  const query = enrollmentSearch.value.trim().toLowerCase()
  return enrollments.value.filter((enrollment) => {
    const status = Number(enrollment.status)
    const isActive = [
      TRAINING_ENROLLMENT_STATUS_PENDING_APPROVAL,
      TRAINING_ENROLLMENT_STATUS_PENDING_SELECTION,
      TRAINING_ENROLLMENT_STATUS_ENROLLED
    ].includes(status)

    const matchesView = selectedEnrollmentView.value === 'all'
      || (selectedEnrollmentView.value === 'active' && isActive)
      || (selectedEnrollmentView.value === 'history' && !isActive)

    const matchesSearch = !query || [enrollment.trainingActionName, enrollment.trainingCategoryName, enrollment.resultValue]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query))

    return matchesView && matchesSearch
  })
})

const progressSummary = computed(() => {
  if (progressData.value) {
    return {
      completed: Number(progressData.value.completed || 0),
      inProgress: Number(progressData.value.inProgress || 0),
      pending: Number(progressData.value.pending || 0),
      successRate: Number(progressData.value.successRate || 0)
    }
  }

  const completed = enrollments.value.filter((enrollment) => Number(enrollment.status) === TRAINING_ENROLLMENT_STATUS_COMPLETED).length
  const inProgress = enrollments.value.filter((enrollment) =>
    [TRAINING_ENROLLMENT_STATUS_ENROLLED, TRAINING_ENROLLMENT_STATUS_PENDING_SELECTION].includes(Number(enrollment.status))
  ).length
  const pending = enrollments.value.filter((enrollment) => Number(enrollment.status) === TRAINING_ENROLLMENT_STATUS_PENDING_APPROVAL).length
  const evaluable = enrollments.value.filter((enrollment) =>
    [
      TRAINING_ENROLLMENT_STATUS_COMPLETED,
      TRAINING_ENROLLMENT_STATUS_NOT_PASS,
      TRAINING_ENROLLMENT_STATUS_NO_SHOW
    ].includes(Number(enrollment.status))
  )
  const successRate = evaluable.length === 0 ? 0 : Math.round((completed / evaluable.length) * 100)

  return {
    completed,
    inProgress,
    pending,
    successRate
  }
})

const categoryFilterOptions = computed(() => {
  const categories = Array.from(new Set(
    catalog.value
      .map((action) => action.trainingCategoryName)
      .filter(Boolean)
  )).sort((a, b) => String(a).localeCompare(String(b)))

  return [
    { label: t('wkt.client.training.filters.all_categories'), value: 'all' },
    ...categories.map((category) => ({
      label: String(category),
      value: String(category)
    }))
  ]
})

const modeFilterOptions = computed(() => ([
  { label: t('wkt.client.training.filters.all_modes'), value: 'all' },
  { label: t('wkt.client.training.mode.presential'), value: '0' },
  { label: t('wkt.client.training.mode.online'), value: '1' },
  { label: t('wkt.client.training.mode.mixed'), value: '2' }
]))

const statusFilterOptions = computed(() => ([
  { label: t('wkt.client.training.filters.all_statuses'), value: 'all' },
  { label: t('wkt.client.training.action_status.planned'), value: '0' },
  { label: t('wkt.client.training.action_status.open'), value: '1' },
  { label: t('wkt.client.training.action_status.closed'), value: '2' },
  { label: t('wkt.client.training.action_status.in_progress'), value: '3' }
]))

const enrollmentViewOptions = computed(() => ([
  { label: t('wkt.client.training.enrollments.filter.active'), value: 'active' },
  { label: t('wkt.client.training.enrollments.filter.history'), value: 'history' },
  { label: t('wkt.client.training.enrollments.filter.all'), value: 'all' }
]))

const progressByCategory = computed(() => {
  if (progressData.value?.categories?.length) {
    return progressData.value.categories.map((item: any) => ({
      key: item.key,
      label: item.label,
      total: Number(item.total || 0),
      completed: Number(item.completed || 0),
      percent: Number(item.percent || 0),
      courses: Array.isArray(item.courses)
        ? item.courses.map((course: any) => ({
          courseId: Number(course.courseId || 0),
          label: String(course.label || ''),
          completed: Boolean(course.completed)
        }))
        : []
    }))
  }

  const categories = new Map<string, { key: string; label: string; total: number; completed: number; percent: number }>()

  enrollments.value.forEach((enrollment) => {
    const label = enrollment.trainingCategoryName || t('wkt.client.training.progress.without_category')
    const key = String(label)

    if (!categories.has(key)) {
      categories.set(key, { key, label, total: 0, completed: 0, percent: 0 })
    }

    const current = categories.get(key)!
    current.total += 1
    if (Number(enrollment.status) === TRAINING_ENROLLMENT_STATUS_COMPLETED) {
      current.completed += 1
    }
  })

  return Array.from(categories.values())
    .map((item) => ({
      ...item,
      percent: item.total === 0 ? 0 : Math.round((item.completed / item.total) * 100),
      courses: []
    }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label))
})

function isProgressCategoryExpanded(key: string): boolean {
  return expandedProgressCategoryKeys.value.includes(String(key))
}

function toggleProgressCategory(key: string): void {
  const normalizedKey = String(key)
  expandedProgressCategoryKeys.value = isProgressCategoryExpanded(normalizedKey)
    ? expandedProgressCategoryKeys.value.filter((item) => item !== normalizedKey)
    : [...expandedProgressCategoryKeys.value, normalizedKey]
}

function formatSimpleDate(simpleDate: any): string {
  if (!simpleDate) {
    return ''
  }
  const converted = convertSimpleDateToDate(simpleDate)
  return converted ? formatOnlyDate(converted) : ''
}

function formatDateRange(fromDate: any, toDate: any): string {
  const fromText = formatSimpleDate(fromDate)
  const toText = formatSimpleDate(toDate)
  if (fromText && toText) {
    return `${fromText} - ${toText}`
  }
  return fromText || toText || ''
}

function formatHours(value: any): string {
  if (value == null || value === '') {
    return ''
  }
  return `${value} h`
}

function formatMode(mode: number | null | undefined): string {
  switch (Number(mode)) {
    case 0:
      return t('wkt.client.training.mode.presential')
    case 1:
      return t('wkt.client.training.mode.online')
    case 2:
      return t('wkt.client.training.mode.mixed')
    default:
      return t('wkt.client.training.mode.unspecified')
  }
}

function getRemainingSeats(action: any): number | null {
  const maxSeats = action?.maxSeats
  const activeEnrollmentsCount = Number(action?.activeEnrollmentsCount || 0)
  if (maxSeats == null) {
    return null
  }
  return Math.max(Number(maxSeats) - activeEnrollmentsCount, 0)
}

function getCapacityText(action: any): string {
  const minSeats = action?.minSeats
  const maxSeats = action?.maxSeats

  if (minSeats == null && maxSeats == null) {
    return ''
  }
  if (minSeats != null && maxSeats != null) {
    return `${minSeats} - ${maxSeats}`
  }
  if (maxSeats != null) {
    return t('wkt.client.training.capacity.max_only', { seats: maxSeats })
  }
  return t('wkt.client.training.capacity.min_only', { seats: minSeats })
}

function clearCatalogFilters(): void {
  catalogSearch.value = ''
  selectedCategoryFilter.value = 'all'
  selectedModeFilter.value = 'all'
  selectedStatusFilter.value = 'all'
}

function getThumbnailUrl(thumbnailPath: string): string {
  return getPublicAssetUrl(thumbnailPath)
}

function getActionInitial(name: string): string {
  if (!name) {
    return 'F'
  }
  return name.trim().charAt(0).toUpperCase()
}

function getTrainingCategoryGradient(categoryName: string | null | undefined): string {
  const normalized = String(categoryName || '').toLowerCase()

  if (normalized.includes('idiom') || normalized.includes('lingu') || normalized.includes('limbi')) {
    return 'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)'
  }
  if (normalized.includes('tecn') || normalized.includes('technic') || normalized.includes('competen')) {
    return 'linear-gradient(135deg, #0f766e 0%, #34d399 100%)'
  }
  if (normalized.includes('direct') || normalized.includes('manager')) {
    return 'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)'
  }
  if (normalized.includes('dato') || normalized.includes('data')) {
    return 'linear-gradient(135deg, #b45309 0%, #fbbf24 100%)'
  }
  if (normalized.includes('medio') || normalized.includes('environment') || normalized.includes('ambient')) {
    return 'linear-gradient(135deg, #166534 0%, #4ade80 100%)'
  }
  return 'linear-gradient(135deg, #334155 0%, #94a3b8 100%)'
}

function formatActionStatus(status: number): string {
  switch (Number(status)) {
    case 1:
      return t('wkt.client.training.action_status.open')
    case 2:
      return t('wkt.client.training.action_status.closed')
    case 3:
      return t('wkt.client.training.action_status.in_progress')
    default:
      return t('wkt.client.training.action_status.planned')
  }
}

function formatEnrollmentStatus(status: number): string {
  switch (Number(status)) {
    case TRAINING_ENROLLMENT_STATUS_PENDING_APPROVAL:
      return t('wkt.client.training.enrollment_status.pending_approval')
    case TRAINING_ENROLLMENT_STATUS_PENDING_SELECTION:
      return t('wkt.client.training.enrollment_status.pending_selection')
    case TRAINING_ENROLLMENT_STATUS_ENROLLED:
      return t('wkt.client.training.enrollment_status.enrolled')
    case TRAINING_ENROLLMENT_STATUS_REJECTED:
      return t('wkt.client.training.enrollment_status.rejected')
    case TRAINING_ENROLLMENT_STATUS_COMPLETED:
      return t('wkt.client.training.enrollment_status.completed')
    case TRAINING_ENROLLMENT_STATUS_NOT_PASS:
      return t('wkt.client.training.enrollment_status.not_pass')
    case TRAINING_ENROLLMENT_STATUS_NO_SHOW:
      return t('wkt.client.training.enrollment_status.no_show')
    case TRAINING_ENROLLMENT_STATUS_CANCELED:
      return t('wkt.client.training.enrollment_status.canceled')
    default:
      return String(status)
  }
}

function getEnrollmentSeverity(status: number): string {
  switch (Number(status)) {
    case TRAINING_ENROLLMENT_STATUS_COMPLETED:
      return 'success'
    case TRAINING_ENROLLMENT_STATUS_PENDING_APPROVAL:
    case TRAINING_ENROLLMENT_STATUS_PENDING_SELECTION:
      return 'warn'
    case TRAINING_ENROLLMENT_STATUS_REJECTED:
    case TRAINING_ENROLLMENT_STATUS_NOT_PASS:
    case TRAINING_ENROLLMENT_STATUS_NO_SHOW:
    case TRAINING_ENROLLMENT_STATUS_CANCELED:
      return 'danger'
    default:
      return 'info'
  }
}

function getCurrentEnrollment(actionId: number): any | null {
  return activeEnrollmentsByActionId.value.get(Number(actionId)) || null
}

function getAvailabilityText(action: any): string {
  if (getCurrentEnrollment(action.id)) {
    return t('wkt.client.training.availability.already_enrolled')
  }

  if (!isCorporate.value) {
    return t('wkt.client.training.availability.read_only')
  }

  if (action?.prerequisiteRequired && !action?.prerequisiteMet) {
    return t('wkt.client.training.availability.prerequisite')
  }

  if (Number(action.status) === 0) {
    return t('wkt.client.training.availability.upcoming')
  }

  if (Number(action.status) === 2) {
    return t('wkt.client.training.availability.closed')
  }

  const remainingSeats = getRemainingSeats(action)
  if (remainingSeats !== null && remainingSeats <= 0) {
    return t('wkt.client.training.availability.full')
  }

  if (remainingSeats !== null && remainingSeats <= 3) {
    return t('wkt.client.training.availability.last_places')
  }

  return t('wkt.client.training.availability.available')
}

function getAvailabilityClass(action: any): string {
  if (getCurrentEnrollment(action.id)) {
    return 'availability-chip--info'
  }

  if (!isCorporate.value) {
    return 'availability-chip--muted'
  }

  if (action?.prerequisiteRequired && !action?.prerequisiteMet) {
    return 'availability-chip--danger'
  }

  if (Number(action.status) === 0 || Number(action.status) === 2) {
    return 'availability-chip--muted'
  }

  const remainingSeats = getRemainingSeats(action)
  if (remainingSeats !== null && remainingSeats <= 0) {
    return 'availability-chip--danger'
  }

  if (remainingSeats !== null && remainingSeats <= 3) {
    return 'availability-chip--warning'
  }

  return 'availability-chip--success'
}

function getEnrollButtonLabel(action: any): string {
  if (!action) {
    return t('wkt.client.training.action.enroll')
  }
  return action.requiresApproval
    ? t('wkt.client.training.action.request_enrollment')
    : t('wkt.client.training.action.enroll')
}

function getEnrollmentDialogTitle(): string {
  return pendingEnrollmentAction.value?.requiresApproval
    ? t('wkt.client.training.enroll_dialog.title_request')
    : t('wkt.client.training.enroll_dialog.title_enroll')
}

function getEnrollmentDialogDescription(): string {
  return pendingEnrollmentAction.value?.requiresApproval
    ? t('wkt.client.training.enroll_dialog.description_request')
    : t('wkt.client.training.enroll_dialog.description_enroll')
}

function shouldShowEnrollButton(action: any): boolean {
  return isCorporate.value && canEnrollAction(action)
}

function canEnrollAction(action: any): boolean {
  if (!isCorporate.value) {
    return false
  }
  if (getCurrentEnrollment(action.id)) {
    return false
  }
  if (action?.prerequisiteRequired && !action?.prerequisiteMet) {
    return false
  }
  const remainingSeats = getRemainingSeats(action)
  if (remainingSeats !== null && remainingSeats <= 0) {
    return false
  }
  if (![TRAINING_ACTION_STATUS_OPEN, TRAINING_ACTION_STATUS_IN_PROGRESS].includes(Number(action.status))) {
    return false
  }
  return true
}

function getPrerequisiteText(action: any): string {
  if (!action?.prerequisiteCourseName) {
    return ''
  }
  if (action?.prerequisiteProgramName) {
    return t('wkt.client.training.prerequisite.detail_with_program', {
      course: action.prerequisiteCourseName,
      program: action.prerequisiteProgramName
    })
  }
  return t('wkt.client.training.prerequisite.detail', {
    course: action.prerequisiteCourseName
  })
}

function getSequenceText(action: any): string {
  if (!action?.sequenceOrder || !action?.sequenceTotalCourses) {
    return ''
  }
  return t('wkt.client.training.sequence.detail', {
    current: action.sequenceOrder,
    total: action.sequenceTotalCourses
  })
}

function sanitizeRichText(html: string): string {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+=(["']).*?\1/gi, '')
    .replace(/\son\w+=([^\s>]+)/gi, '')
    .replace(/javascript:/gi, '')
}

function getObjectivesProgramHtml(action: any): string {
  const html = typeof action?.objectivesProgram === 'string' ? action.objectivesProgram.trim() : ''
  if (!html) {
    return ''
  }
  return sanitizeRichText(html)
}

function getApiErrorMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) {
    return null
  }

  const candidate = (error as {
    response?: {
      data?: {
        message?: unknown
      }
    }
  }).response?.data?.message

  return typeof candidate === 'string' && candidate.trim() ? candidate : null
}

function loadCatalog(): void {
  isLoadingCatalog.value = true
  worktimeApi.getTrainingCatalog(
    (data) => {
      catalog.value = Array.isArray(data) ? data : []
      isLoadingCatalog.value = false
    },
    (error) => {
      isLoadingCatalog.value = false
      showAlert({
        text: getApiErrorMessage(error) || t('wkt.client.training.error.load_catalog'),
        color: 'error'
      })
    }
  )
}

function loadEnrollments(): void {
  isLoadingEnrollments.value = true
  worktimeApi.getMyTrainingEnrollments(
    (data) => {
      enrollments.value = Array.isArray(data) ? data : []
      isLoadingEnrollments.value = false
    },
    (error) => {
      isLoadingEnrollments.value = false
      showAlert({
        text: getApiErrorMessage(error) || t('wkt.client.training.error.load_enrollments'),
        color: 'error'
      })
    }
  )
}

function loadProgress(): void {
  isLoadingProgress.value = true
  worktimeApi.getMyTrainingProgress(
    (data) => {
      progressData.value = data
      expandedProgressCategoryKeys.value = []
      isLoadingProgress.value = false
    },
    () => {
      progressData.value = null
      expandedProgressCategoryKeys.value = []
      isLoadingProgress.value = false
    }
  )
}

function openActionDetail(actionId: number): void {
  detailDialogVisible.value = true
  selectedActionDetail.value = null
  isLoadingDetail.value = true
  worktimeApi.getTrainingAction(
    actionId,
    (data) => {
      selectedActionDetail.value = data
      isLoadingDetail.value = false
    },
    (error) => {
      isLoadingDetail.value = false
      showAlert({
        text: getApiErrorMessage(error) || t('wkt.client.training.error.load_detail'),
        color: 'error'
      })
    }
  )
}

function openEnrollmentDialog(action: any): void {
  if (!canEnrollAction(action)) {
    return
  }

  pendingEnrollmentAction.value = action
  pendingEnrollmentObservations.value = ''
  enrollmentDialogVisible.value = true
}

function closeEnrollmentDialog(): void {
  enrollmentDialogVisible.value = false
  pendingEnrollmentAction.value = null
  pendingEnrollmentObservations.value = ''
  isSubmittingEnrollment.value = false
}

function submitEnrollment(): void {
  if (!pendingEnrollmentAction.value || !canEnrollAction(pendingEnrollmentAction.value) || isSubmittingEnrollment.value) {
    return
  }

  isSubmittingEnrollment.value = true
  worktimeApi.enrollTrainingAction(
    {
      trainingActionId: pendingEnrollmentAction.value.id,
      observations: pendingEnrollmentObservations.value.trim() || null
    },
    (data) => {
      closeEnrollmentDialog()
      detailDialogVisible.value = false
      showAlert({
        text: data?.message || t('wkt.client.training.success.enrolled'),
        color: 'success'
      })
      loadCatalog()
      loadEnrollments()
      loadProgress()
    },
    (error) => {
      isSubmittingEnrollment.value = false
      showAlert({
        text: getApiErrorMessage(error) || t('wkt.client.training.error.enroll'),
        color: 'error'
      })
    }
  )
}

function downloadEnrollmentCertificate(enrollment: any): void {
  if (!enrollment?.id || !enrollment?.certificateDocumentId) {
    return
  }

  gwtApi.downloadFile(
    `user/training/enrollment/${enrollment.id}/certificate`,
    enrollment.certificateDocumentName || `training-certificate-${enrollment.id}`
  )
}

function loadModuleData(): void {
  const sessionInfo = gwtApi.getSessionInformation()
  if (!sessionInfo) {
    setTimeout(loadModuleData, 1000)
    return
  }
  sessionInformation.value = sessionInfo
  loadCatalog()
  loadEnrollments()
  loadProgress()
}

function onReveal(): void {
  showModule.value = true
  loadModuleData()
}

function refreshModule(): void {
  clearCatalogFilters()
  loadModuleData()
}

function open(): void {
  onReveal()
}

watch(detailDialogVisible, (visible) => {
  if (!visible) {
    selectedActionDetail.value = null
  }
})

defineExpose({
  open,
  onReveal,
  refreshModule
})
</script>

<style scoped>
.training-module {
  width: 100%;
  height: auto;
  min-height: 0 !important;
  background: var(--white);
  overflow-y: visible;
  overflow-x: hidden;
  box-sizing: border-box;
  padding: 0 0 8px;
}

.training-container {
  padding: 0 0.5rem 1rem;
}

.training-content {
  max-width: 1280px;
  margin: 0 auto;
}

.toolbar-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.search-input {
  width: 100%;
  max-width: 420px;
}

.catalog-filters {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  flex: 1;
}

.catalog-select {
  min-width: 180px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40vh;
}

.card-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.catalog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
}

.training-card {
  border: 1px solid var(--border-color);
  box-shadow: none;
  overflow: hidden;
  height: 100%;
}

.training-card-media {
  aspect-ratio: 16 / 9;
  min-height: 196px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.training-card-media--clickable {
  cursor: pointer;
}

.training-card-media--clickable:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: -2px;
}

.training-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.training-card-fallback {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  backdrop-filter: blur(4px);
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.card-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--black);
}

.card-title--clickable {
  cursor: pointer;
}

.card-title--clickable:hover {
  color: var(--primary-color);
}

.card-title--clickable:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
  border-radius: 4px;
}

.card-subtitle {
  margin-top: 0.25rem;
  color: var(--grey-text);
  font-size: 0.95rem;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.action-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.detail-chip-row {
  margin-bottom: 1.25rem;
}

.detail-row {
  display: flex;
  gap: 0.5rem;
}

.detail-label {
  min-width: 150px;
  font-weight: 600;
  color: #495057;
}

.detail-value {
  color: var(--grey-text);
  flex: 1;
}

.description {
  margin: 1rem 0 0;
  color: var(--grey-text);
  line-height: 1.5;
}

.detail-description {
  margin-top: 1.25rem;
}

.detail-rich-text {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border-color);
}

.detail-rich-text__title {
  margin: 0 0 0.85rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--black);
}

.detail-rich-text__content {
  color: var(--grey-text);
  line-height: 1.6;
}

.detail-rich-text__content :deep(p) {
  margin: 0 0 0.85rem;
}

.detail-rich-text__content :deep(ul),
.detail-rich-text__content :deep(ol) {
  margin: 0 0 0.85rem 1.25rem;
  padding: 0;
}

.detail-rich-text__content :deep(li) {
  margin-bottom: 0.35rem;
}

.detail-rich-text__content :deep(h1),
.detail-rich-text__content :deep(h2),
.detail-rich-text__content :deep(h3) {
  margin: 1rem 0 0.65rem;
  color: var(--black);
  line-height: 1.25;
}

.progress-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.progress-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.summary-card {
  border: 1px solid var(--border-color);
  box-shadow: none;
}

.summary-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
}

.summary-label {
  margin-top: 0.35rem;
  color: var(--grey-text);
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.progress-row {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--border-color);
}

.progress-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.progress-row-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  color: var(--black);
}

.progress-row-actions {
  display: flex;
  justify-content: flex-start;
}

.training-progress-toggle.p-button {
  padding-left: 0 !important;
}

.progress-course-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0 0.25rem 1rem;
}

.progress-course-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}

.progress-course-label {
  color: var(--grey-text);
  flex: 1;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.training-link-button.p-button,
.training-toolbar-button.p-button {
  color: var(--primary-color) !important;
  border-color: transparent !important;
}

.training-link-button.p-button:hover,
.training-link-button.p-button:focus,
.training-link-button.p-button:active,
.training-toolbar-button.p-button:hover,
.training-toolbar-button.p-button:focus,
.training-toolbar-button.p-button:active {
  background: rgba(var(--primary-rgb), 0.08) !important;
  color: var(--primary-color) !important;
  border-color: transparent !important;
}

.training-link-button.p-button .p-button-icon,
.training-toolbar-button.p-button .p-button-icon,
.training-link-button.p-button .p-button-label,
.training-toolbar-button.p-button .p-button-label {
  color: var(--primary-color) !important;
}

.training-primary-button.p-button {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.training-primary-button.p-button:hover {
  background: var(--primary-dark-color);
  border-color: var(--primary-dark-color);
}

.helper-text {
  display: block;
  margin-top: 0.75rem;
  color: var(--grey-text);
}

.availability-chip {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
}

.availability-chip--success {
  background: #dcfce7;
  color: #166534;
}

.availability-chip--danger {
  background: #fee2e2;
  color: #991b1b;
}

.availability-chip--info {
  background: #dbeafe;
  color: #1d4ed8;
}

.availability-chip--warning {
  background: #fef3c7;
  color: #92400e;
}

.availability-chip--muted {
  background: #e5e7eb;
  color: #374151;
}

@media (max-width: 768px) {
  .training-module {
    min-height: 0 !important;
    height: auto;
    overflow: visible;
    padding-bottom: 0;
  }

  .training-container {
    padding: 0.75rem 1rem 0;
  }

  .toolbar-row,
  .card-title-row,
  .detail-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .catalog-filters {
    width: 100%;
    gap: 0.875rem;
  }

  .catalog-grid {
    display: flex;
    grid-template-columns: none;
  }

  .catalog-select,
  .search-input {
    max-width: none;
    width: 100%;
  }

  .detail-label {
    min-width: auto;
  }
}

@media (min-width: 769px) {
  .training-module {
    height: auto;
    min-height: 0 !important;
    overflow-y: visible;
    overflow-x: hidden;
    padding-bottom: 16px;
  }
}

@media print {
  .training-module {
    display: none !important;
  }
}
</style>
